/**
 * components/riskTerminal/hoverCard.jsx
 *
 * upstox-074 (item B) — the ONE hover card for the whole Risk Terminal screen. Every element that
 * wants one carries `data-tip="text"` (lines split on "\n"), optionally `data-tip-head="HEADING"`
 * and `data-tip-tone="red|amber"` (the heading takes the rule colour; the body stays ink). No native
 * `title` attribute is used on this screen (a source test holds that). One <HoverLayer/> listens for
 * the whole page, so every trigger is the same: opens after ~250 ms, 6 px under the trigger,
 * left-aligned to it (flips above near the bottom of the viewport), closes on pointer leave, no arrow.
 *
 * A trigger whose value is the thing to explain adds the class `rt-tip` (dotted underline, default
 * cursor — the style in SCOPED_CSS); buttons and icons get the card without the underline.
 * `data-tip-open="true"` keeps one card open (the dev harness's screenshots).
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { C, FONT } from './theme';

export const HOVER_DELAY_MS = 250;
const GAP = 6;
const TONE = { red: C.red, amber: C.amber };

/** Wrap a value so it carries a hover card (and the dotted-underline trigger when `dotted`). */
export function Tip({ text, head, tone, dotted, children, style, as: Tag = 'span', open, ...rest }) {
  return (
    <Tag className={dotted ? 'rt-tip' : undefined} data-tip={text || undefined} data-tip-head={head || undefined}
      data-tip-tone={tone || undefined} data-tip-open={open ? 'true' : undefined} style={style} {...rest}>{children}</Tag>
  );
}

function read(el) {
  return { text: el.getAttribute('data-tip'), head: el.getAttribute('data-tip-head'), tone: el.getAttribute('data-tip-tone') };
}

export function HoverLayer() {
  const [card, setCard] = useState(null);                  // { el, text, head, tone, x, y }
  const timer = useRef(null);
  const cur = useRef(null);
  const ref = useRef(null);

  useEffect(() => {
    const trigger = t => (t && t.closest ? t.closest('[data-tip]') : null);
    const inScreen = el => !!(el && el.closest && el.closest('.rt-root'));
    const hide = () => { clearTimeout(timer.current); cur.current = null; setCard(null); };
    const over = e => {
      const el = trigger(e.target);
      if (!el || !inScreen(el) || !el.getAttribute('data-tip')) { if (cur.current) hide(); return; }
      if (el === cur.current) return;
      clearTimeout(timer.current);
      cur.current = el;
      timer.current = setTimeout(() => {
        const r = el.getBoundingClientRect();
        setCard({ el, ...read(el), x: r.left, y: r.bottom + GAP, top: r.top });
      }, HOVER_DELAY_MS);
    };
    const out = e => {
      const el = cur.current;
      if (!el) return;
      if (e.relatedTarget && el.contains(e.relatedTarget)) return;
      hide();
    };
    document.addEventListener('mouseover', over, true);
    document.addEventListener('mouseout', out, true);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      clearTimeout(timer.current);
      document.removeEventListener('mouseover', over, true);
      document.removeEventListener('mouseout', out, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  // the harness keeps one card open: [data-tip-open="true"]
  useEffect(() => {
    const el = document.querySelector('.rt-root [data-tip-open="true"][data-tip]');
    if (el) { const r = el.getBoundingClientRect(); setCard({ el, ...read(el), x: r.left, y: r.bottom + GAP, top: r.top, pinned: true }); }
  }, []);

  // placement: left-aligned to the trigger, kept inside the viewport, flipped above near the bottom
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!card || !ref.current) { setPos(null); return; }
    const h = ref.current.offsetHeight, w = ref.current.offsetWidth;
    const flip = card.y + h > window.innerHeight - 4 && card.top - GAP - h > 4;
    setPos({ left: Math.max(4, Math.min(card.x, window.innerWidth - w - 4)), top: flip ? card.top - GAP - h : card.y, flipped: flip });
  }, [card]);

  if (!card) return null;
  const lines = String(card.text).split('\n');
  return createPortal(
    <div ref={ref} data-hover-card data-flipped={pos && pos.flipped ? 'true' : undefined} role="tooltip"
      style={{ position: 'fixed', left: pos ? pos.left : card.x, top: pos ? pos.top : card.y, zIndex: 1000, maxWidth: '280px', boxSizing: 'border-box',
        padding: '9px 12px', background: '#fff', border: '1px solid #cfc9bd', borderRadius: 0, boxShadow: '0 8px 20px -8px rgba(26,25,23,.28)',
        fontFamily: FONT, color: C.ink, pointerEvents: 'none', visibility: pos ? 'visible' : 'hidden' }}>
      {card.head && <div data-hover-head style={{ fontSize: '9.5px', letterSpacing: '0.14em', fontWeight: 700, color: TONE[card.tone] || C.ink4, marginBottom: '3px' }}>{card.head}</div>}
      {lines.map((ln, i) => <div key={i} style={{ fontSize: '12px', lineHeight: 1.5, color: C.ink }}>{ln}</div>)}
    </div>,
    document.body);
}
